import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { VideoCamera } from "@phosphor-icons/react";
import { LOADING_MESSAGES, CHUNKED_LOADING_MESSAGES } from "@/components/talkingdoc/talkingDocConstants";
import { TalkingDocForm } from "@/components/talkingdoc/TalkingDocForm";
import { TalkingDocOutput } from "@/components/talkingdoc/TalkingDocOutput";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";

export default function TalkingDocPage() {
  const { api, refreshUser } = useAuth();

  const [goal, setGoal] = useState("");
  const [audience, setAudience] = useState("");
  const [title, setTitle] = useState("");
  const [minutes, setMinutes] = useState(10);
  const [toneMode, setToneMode] = useState("");
  const [toneVariations, setToneVariations] = useState([]);
  const [offerCta, setOfferCta] = useState("");
  const [videoObjective, setVideoObjective] = useState("");
  const [audienceContext, setAudienceContext] = useState("");
  const [mustInclude, setMustInclude] = useState("");
  const [avoidSaying, setAvoidSaying] = useState("");
  const [aiModel, setAiModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [includeTimestampsScript, setIncludeTimestampsScript] = useState(false);
  const [includeTimestampsOutline, setIncludeTimestampsOutline] = useState(false);
  const [includeVisualCuesScript, setIncludeVisualCuesScript] = useState(false);
  const [researchNotes, setResearchNotes] = useState("");
  const [researching, setResearching] = useState(false);
  const [researchMode, setResearchMode] = useState("standard");
  const [documentPurpose, setDocumentPurpose] = useState("lead_magnet");
  const [referenceVideo, setReferenceVideo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [result, setResult] = useState(null);
  const [violations, setViolations] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [editText, setEditText] = useState("");
  const [rewritingSection, setRewritingSection] = useState(null);
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [exportTemplates, setExportTemplates] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [brandPalette, setBrandPalette] = useState(null);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setAiModel(resp.data.models[0]);
      } catch (err) { console.warn("Failed to load available models:", err); }
    };
    fetchModels();
  }, [api]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await api.get("/talking-doc/export-templates");
        setExportTemplates(resp.data.script_templates || resp.data.templates || []);
      } catch (err) { console.warn("Failed to load talking-doc templates:", err); }
    };
    fetchTemplates();
  }, [api]);

  useEffect(() => {
    const fetchBrandPalette = async () => {
      try {
        const resp = await api.get("/settings/profile/brand-palette");
        if (resp.data.brand_palette) setBrandPalette(resp.data.brand_palette);
      } catch (err) { console.warn("Failed to load brand palette:", err); }
    };
    fetchBrandPalette();
  }, [api]);

  useEffect(() => {
    if (!generating) return;
    const msgs = minutes > 15 ? CHUNKED_LOADING_MESSAGES : LOADING_MESSAGES;
    const interval_ms = minutes > 15 ? 6000 : 4000;
    let i = 0;
    setLoadingMsg(msgs[0]);
    const interval = setInterval(() => {
      i = Math.min(i + 1, msgs.length - 1);
      setLoadingMsg(msgs[i]);
    }, interval_ms);
    return () => clearInterval(interval);
  }, [generating, minutes]);

  const handleResearch = async () => {
    if (!title.trim() || title.trim().length < 6) { toast.error("Enter a topic (6+ characters) to research"); return; }
    if (!aiModel) { toast.error("Select an AI model first"); return; }
    setResearching(true);
    try {
      const resp = await api.post("/talking-doc/research", {
        title: title.trim(), goal: goal || "Educate", audience: audience || "Warm",
        minutes, ai_model: aiModel, research_mode: researchMode,
      });
      if (resp.data.research) {
        setResearchNotes(resp.data.research);
        toast.success("Research complete — review and edit before generating");
      } else { toast.error("Research returned empty results"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Research failed");
    } finally { setResearching(false); }
  };

  const handleGenerate = async () => {
    if (!title.trim()) { toast.error("Topic/Title is required"); return; }
    if (!goal) { toast.error("Video Goal is required"); return; }
    if (!audience) { toast.error("Audience is required"); return; }
    if (!toneMode) { toast.error("Tone Mode is required"); return; }
    if (!aiModel) { toast.error("Select an AI model"); return; }

    setGenerating(true); setResult(null); setViolations({});
    try {
      const resp = await api.post("/talking-doc/generate", {
        goal, audience, title: title.trim(), minutes, tone_mode: toneMode,
        tone_variations: toneVariations,
        offer_cta: offerCta.trim(), video_objective: videoObjective.trim(),
        audience_context: audienceContext.trim(),
        must_include: mustInclude.split("\n").map(s => s.trim()).filter(Boolean),
        avoid_saying: avoidSaying.split("\n").map(s => s.trim()).filter(Boolean),
        include_timestamps_script: includeTimestampsScript,
        include_timestamps_outline: includeTimestampsOutline,
        include_visual_cues_script: includeVisualCuesScript,
        document_purpose: documentPurpose,
        reference_video: referenceVideo,
        research_notes: researchNotes.trim(),
        ai_model: aiModel,
      });

      if (resp.data.status === "processing" && resp.data.job_id) {
        const jobId = resp.data.job_id;
        let polls = 0;
        const poll = async () => {
          try {
            const statusResp = await api.get(`/talking-doc/status/${jobId}`);
            const job = statusResp.data;
            if (job.status === "complete") {
              setResult(job); setViolations(job.violations || {});
              toast.success(`Talking Doc generated! ${job.metadata?.word_count || 0} words`);
              refreshUser(); setGenerating(false); return;
            }
            if (job.status === "error") { toast.error(job.message || "Generation failed"); setGenerating(false); return; }
            if (job.progress) setLoadingMsg(job.progress);
            polls++;
            if (polls < 120) setTimeout(poll, 3000);
            else { toast.error("Generation timed out. Please try again."); setGenerating(false); }
          } catch { toast.error("Failed to check generation status"); setGenerating(false); }
        };
        setTimeout(poll, 3000);
      } else if (resp.data.error) {
        toast.error(resp.data.message || "Generation failed"); setGenerating(false);
      } else {
        setResult(resp.data); setViolations(resp.data.violations || {});
        toast.success(`Talking Doc generated! ${resp.data.metadata?.word_count || 0} words`);
        refreshUser(); setGenerating(false);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); setGenerating(false); }
  };

  const handleRegenerateSection = useCallback(async (sectionLabel) => {
    if (!result) return;
    setRegeneratingSection(sectionLabel);
    try {
      const reason = window.prompt("Why regenerate? (optional)", "Fresh take");
      const resp = await api.post("/talking-doc/regenerate-section", {
        section_label: sectionLabel, reason: reason || "Fresh take", original_context_summary: title,
        goal, audience, minutes, tone_mode: toneMode,
        must_include: mustInclude.split("\n").map(s => s.trim()).filter(Boolean),
        avoid_saying: avoidSaying.split("\n").map(s => s.trim()).filter(Boolean), ai_model: aiModel,
      });
      if (resp.data.content) {
        setResult(prev => ({ ...prev, sections: { ...prev.sections, [sectionLabel]: resp.data.content } }));
        toast.success(`${sectionLabel} regenerated`);
      }
    } catch { toast.error("Section regeneration failed"); }
    finally { setRegeneratingSection(null); }
  }, [result, api, title, goal, audience, minutes, toneMode, mustInclude, avoidSaying, aiModel]);

  const handleRewriteChip = useCallback(async (sectionLabel, chip) => {
    if (!result?.sections?.[sectionLabel]) return;
    setRewritingSection(`${sectionLabel}-${chip}`);
    let timeBudget = null;
    if (chip === "shorten_to_time") {
      const val = window.prompt("Target time in seconds:", "30");
      if (!val) { setRewritingSection(null); return; }
      timeBudget = parseInt(val, 10);
    }
    try {
      const resp = await api.post("/talking-doc/rewrite", { text: result.sections[sectionLabel], chip, time_budget_seconds: timeBudget, ai_model: aiModel });
      if (resp.data.rewritten_text) {
        setResult(prev => ({ ...prev, sections: { ...prev.sections, [sectionLabel]: resp.data.rewritten_text } }));
        toast.success(`Rewrite applied: ${chip.replace(/_/g, " ")}`);
      }
    } catch { toast.error("Rewrite failed"); }
    finally { setRewritingSection(null); }
  }, [result, api, aiModel]);

  const handleEditSave = (sectionLabel) => {
    setResult(prev => ({ ...prev, sections: { ...prev.sections, [sectionLabel]: editText } }));
    setEditingSection(null); toast.success("Section updated");
  };

  const handleCopyMarkdown = () => {
    if (!result?.sections) return;
    const md = Object.entries(result.sections).map(([k, v]) => `### ${k}\n\n${v}`).join("\n\n---\n\n");
    navigator.clipboard.writeText(md); toast.success("Copied to clipboard");
  };

  const handleCopyOutline = () => {
    const outlineText = result?.document_outline || result?.outline || result?.outline_notes || "";
    if (!outlineText) return;
    navigator.clipboard.writeText(outlineText); toast.success("Document outline copied to clipboard");
  };

  const toStructuredOutlineMarkdown = useCallback((outline) => {
    if (!outline) return "";
    return outline.split("\n").map((line) => {
      if (!line.trim()) return "";
      const indent = line.match(/^\s*/)?.[0] || "";
      const trimmed = line.trim();
      if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) return `${indent}${trimmed}`;
      if (/^[•●▪◦→]\s*/.test(trimmed)) return `${indent}${trimmed.replace(/^[•●▪◦→]\s*/, "- ")}`;
      return `${indent}- ${trimmed}`;
    }).join("\n").trim();
  }, []);

  const downloadTextFile = useCallback((content, fileName, mimeType = "text/plain;charset=utf-8;") => {
    if (!content) return;
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = fileName;
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  }, []);

  const handleCopyStructuredOutline = useCallback(() => {
    if (!result?.outline) return;
    navigator.clipboard.writeText(toStructuredOutlineMarkdown(result.outline));
    toast.success("Structured markdown copied to clipboard");
  }, [result, toStructuredOutlineMarkdown]);

  const handleDownloadOutlineMd = useCallback(() => {
    if (!result?.outline) return;
    downloadTextFile(toStructuredOutlineMarkdown(result.outline), "talking-doc-outline.md", "text/markdown;charset=utf-8;");
    toast.success("Downloaded outline as .md");
  }, [result, toStructuredOutlineMarkdown, downloadTextFile]);

  const handleDownloadOutlineTxt = useCallback(() => {
    if (!result?.outline) return;
    downloadTextFile(result.outline, "talking-doc-outline.txt");
    toast.success("Downloaded outline as .txt");
  }, [result, downloadTextFile]);

  const handleExportOutlineGoogleDocs = useCallback(async () => {
    if (!result?.outline) return;
    try {
      const outlineText = result.document_outline || result.outline || result.outline_notes || "";
      const exportResp = await api.post("/google/export-doc", { title: `Talking Doc Outline: ${title}`, markdown_content: outlineText });
      if (exportResp.data.document_url) window.open(exportResp.data.document_url, "_blank");
      else toast.error("Google Docs export failed");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.includes("not connected")) toast.error("Google account not connected — go to Settings to connect");
      else toast.error("Export failed — ensure Google Docs is connected in Settings");
    }
  }, [api, result, title]);

  const handleExportGoogleDocs = async () => {
    if (!result?.sections) return;
    try {
      const resp = await api.post("/google/export-doc", {
        title: `Talking Doc: ${title}`, sections: result.sections,
        section_names: result.section_names || Object.keys(result.sections),
      });
      if (resp.data.document_url) window.open(resp.data.document_url, "_blank");
      else toast.error("Google Docs export failed");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.includes("not connected")) toast.error("Google account not connected — go to Settings to connect");
      else toast.error("Export failed — ensure Google Docs is connected in Settings");
    }
  };

  const handleExportTemplate = async (templateKey) => {
    if (!result?.sections) return;
    setExporting(true);
    try {
      const resp = await api.post("/talking-doc/format-export", { template: templateKey, sections: result.sections, source_type: "script" });
      if (resp.data.formatted) {
        navigator.clipboard.writeText(resp.data.formatted);
        toast.success(`Copied ${exportTemplates.find(t => t.key === templateKey)?.name || templateKey} format`);
      }
    } catch { toast.error("Export formatting failed"); }
    finally { setExporting(false); }
  };

  const toggleToneVariation = (tone) => {
    setToneVariations(prev => prev.includes(tone) ? prev.filter(t => t !== tone) : [...prev, tone]);
  };

  return (
    <>
      <Header />
      <NoBusinessGuard>
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#FF4400]/10 flex items-center justify-center">
                <VideoCamera size={22} weight="duotone" className="text-[#FF4400]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="talking-doc-title">
                Guided Talking Doc
              </h1>
            </div>
            <p className="text-sm text-[var(--text-muted)] max-w-2xl">
              Build production-ready assets for Loom-style talking-head videos: a Script for spoken delivery and a Document Outline for production handoff (slides, Notion, GDoc, lead magnet).
            </p>
            <div className="mt-4"><AlwaysIncludeIndicator /></div>
          </div>

          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key="form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <TalkingDocForm
                  {...{ goal, setGoal, audience, setAudience, title, setTitle, minutes, setMinutes,
                    toneMode, setToneMode, toneVariations, toggleToneVariation,
                    offerCta, setOfferCta, videoObjective, setVideoObjective,
                    audienceContext, setAudienceContext, mustInclude, setMustInclude, avoidSaying, setAvoidSaying,
                    aiModel, setAiModel, availableModels,
                    includeTimestampsScript, setIncludeTimestampsScript,
                    includeTimestampsOutline, setIncludeTimestampsOutline,
                    includeVisualCuesScript, setIncludeVisualCuesScript,
                    researchNotes, setResearchNotes, researching, handleResearch,
                    researchMode, setResearchMode,
                    documentPurpose, setDocumentPurpose,
                    referenceVideo, setReferenceVideo,
                    generating, loadingMsg, handleGenerate }}
                />
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <TalkingDocOutput
                  result={result} violations={violations} title={title} minutes={minutes}
                  editingSection={editingSection} setEditingSection={setEditingSection}
                  editText={editText} setEditText={setEditText}
                  handleEditSave={handleEditSave} handleRegenerateSection={handleRegenerateSection}
                  regeneratingSection={regeneratingSection} handleRewriteChip={handleRewriteChip}
                  rewritingSection={rewritingSection} handleCopyMarkdown={handleCopyMarkdown}
                  handleCopyOutline={handleCopyOutline} handleCopyStructuredOutline={handleCopyStructuredOutline}
                  handleDownloadOutlineMd={handleDownloadOutlineMd} handleDownloadOutlineTxt={handleDownloadOutlineTxt}
                  handleExportGoogleDocs={handleExportGoogleDocs} handleExportOutlineGoogleDocs={handleExportOutlineGoogleDocs}
                  handleExportTemplate={handleExportTemplate} exportTemplates={exportTemplates}
                  exporting={exporting} brandPalette={brandPalette} onReset={() => setResult(null)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </NoBusinessGuard>
    </>
  );
}
